import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quotes, Star, PaperPlaneTilt, X, CheckCircle, Heart } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default testimonials (shown if no approved ones exist)
const defaultTestimonials = [
  {
    quote: "Adeseun has this incredible ability to make you feel truly heard. In a world where everyone's rushing to give advice, they simply listen — and that's exactly what I needed. I walked away from our conversation feeling lighter and understood.",
    name: "Anonymous",
    initials: "JM"
  },
  {
    quote: "What I appreciate most about Adeseun is how easy they are to talk to. There's no judgment, no pressure — just genuine care. They helped me see my situation clearly and even pointed me toward resources I didn't know existed.",
    name: "Anonymous",
    initials: "TK"
  },
  {
    quote: "I was hesitant to open up at first, but Adeseun created such a safe space that the words just flowed. They validated my feelings and reminded me that it's okay not to be okay. That conversation was a turning point for me.",
    name: "Anonymous",
    initials: "SA"
  },
  {
    quote: "Sometimes you just need someone who gets it — someone who looks like you and understands your experiences. Adeseun is that person. Their support helped me find the courage to take the next step in my healing journey.",
    name: "Anonymous",
    initials: "DO"
  }
];

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ quote: '', name: '', is_anonymous: true });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      if (response.data && response.data.length > 0) {
        // Combine approved testimonials with defaults
        setTestimonials([...response.data, ...defaultTestimonials].slice(0, 8));
      }
    } catch (error) {
      console.log('Using default testimonials');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quote.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        quote: formData.quote,
        name: formData.is_anonymous ? 'Anonymous' : formData.name,
        is_anonymous: formData.is_anonymous
      });
      setSuccess(true);
      setFormData({ quote: '', name: '', is_anonymous: true });
      setTimeout(() => {
        setSuccess(false);
        setShowForm(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to submit testimonial:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="testimonials" className="py-24 sm:py-32 bg-[#2A2825]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-2 rounded-full text-sm mb-6">
            <Star size={16} weight="fill" className="text-[#E28B5E]" />
            Voices from the Community
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-white mb-4">
            You're Not Alone in This
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Hear from others who found support, understanding, and a listening ear through Safe Space.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {testimonials.slice(0, 4).map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300"
              data-testid={`testimonial-${index}`}
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 left-8">
                <div className="w-10 h-10 bg-[#B46C4A] rounded-full flex items-center justify-center">
                  <Quotes size={20} weight="fill" className="text-white" />
                </div>
              </div>

              {/* Quote */}
              <p className="text-white/90 leading-relaxed mt-4 mb-6 italic">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#647B64] rounded-full flex items-center justify-center text-white font-medium">
                  {testimonial.initials}
                </div>
                <div>
                  <p className="text-white font-medium">{testimonial.name}</p>
                  <p className="text-white/50 text-sm">Community Member</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Share Your Story CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white/5 rounded-3xl p-8 max-w-2xl mx-auto border border-white/10">
            <Heart size={40} weight="fill" className="text-[#B46C4A] mx-auto mb-4" />
            <h3 className="text-xl font-['Outfit'] font-semibold text-white mb-2">
              Has Safe Space Helped You?
            </h3>
            <p className="text-white/70 mb-6">
              Your story could inspire someone else to reach out. Share your experience and help others know they're not alone.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 bg-[#B46C4A] hover:bg-[#9B5C3E] text-white px-8 py-4 rounded-full font-medium transition-all duration-300"
              data-testid="share-story-btn"
            >
              <Quotes size={20} weight="fill" />
              Share Your Story
            </button>
          </div>
        </motion.div>

        {/* Testimonial Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
              onClick={() => !submitting && setShowForm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="testimonial-form-modal"
              >
                {success ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} weight="fill" className="text-green-600" />
                    </div>
                    <h3 className="text-2xl font-['Outfit'] font-semibold text-[#2A2825] mb-2">
                      Thank You! 💚
                    </h3>
                    <p className="text-[#5C564F]">
                      Your testimonial has been submitted and will be reviewed shortly.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="relative bg-[#B46C4A] text-white p-6">
                      <button
                        onClick={() => setShowForm(false)}
                        className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                        disabled={submitting}
                      >
                        <X size={18} weight="bold" />
                      </button>
                      <h3 className="text-2xl font-['Outfit'] font-semibold">Share Your Experience</h3>
                      <p className="text-white/80 mt-1 text-sm">
                        Your words could help someone take the first step
                      </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#5C564F] mb-2">
                          Your Story
                        </label>
                        <textarea
                          value={formData.quote}
                          onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                          placeholder="Share how Safe Space or Adeseun has helped you..."
                          rows={5}
                          className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A] resize-none"
                          data-testid="testimonial-quote-input"
                          required
                          disabled={submitting}
                        />
                      </div>

                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="is_anonymous"
                          checked={formData.is_anonymous}
                          onChange={(e) => setFormData({ ...formData, is_anonymous: e.target.checked })}
                          className="w-5 h-5 rounded border-[#E5DCD0] text-[#B46C4A] focus:ring-[#B46C4A]"
                          disabled={submitting}
                        />
                        <label htmlFor="is_anonymous" className="text-sm text-[#5C564F]">
                          Keep my identity anonymous
                        </label>
                      </div>

                      {!formData.is_anonymous && (
                        <div>
                          <label className="block text-sm font-medium text-[#5C564F] mb-2">
                            Your Name (First name only is fine)
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Sarah"
                            className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 text-[#2A2825] placeholder:text-[#5C564F]/50 focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                            data-testid="testimonial-name-input"
                            disabled={submitting}
                          />
                        </div>
                      )}

                      <div className="bg-[#F2EBE1] rounded-2xl p-4 text-sm text-[#5C564F]">
                        <p>
                          <strong>Note:</strong> Your testimonial will be reviewed before being published to ensure it aligns with our community guidelines.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting || !formData.quote.trim()}
                        className="w-full bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all duration-300"
                        data-testid="submit-testimonial-btn"
                      >
                        {submitting ? (
                          <>
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <PaperPlaneTilt size={20} weight="fill" />
                            Submit Testimonial
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TestimonialsSection;
